import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faClock, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useField } from 'formik';
import { useEffect, useState } from 'react';
import { Label } from '../ui/Label';

type Props = {
  label?: string;
  focusValue?: string;
  error?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  leadingIcon?: boolean;
  showClearIcon?: boolean;
};

type State = {
  hour: string;
  minute: string;
  meridiem: string;
};

const HOURS = [...Array(12)].map((_, i) => (1 + i).toString());
const MINUTES = [...Array(4)].map((_, i) =>
  (i * 15).toString().padStart(2, '0')
);

const emptyValue = { hour: '', minute: '', meridiem: 'am' } as const;
const initValue = (value: string) => {
  if (!value) {
    return null;
  }
  const hour = value.substring(0, 2);
  const minute = value.slice(-2);
  const meridiem = parseInt(hour) >= 12 ? 'pm' : 'am';
  const hour12 = parseInt(hour) % 12 || 12;
  return { hour: hour12.toString(), minute, meridiem };
};

export const TimePicker = ({
  label,
  focusValue,
  error,
  value,
  onChange,
  required = false,
  disabled = false,
  leadingIcon = false,
  showClearIcon = false,
}: Props) => {
  const [parts, setParts] = useState<State>(() => {
    return initValue(value) ?? emptyValue;
  });
  const hasValue = Boolean(value);
  const handleClear = () => {
    setParts(emptyValue);
    onChange('');
  };
  const setPart = (k: keyof State) => (v: string) => {
    const next = { ...parts, [k]: v } as State;
    // If user selects an hour and minutes are empty, default minutes to '00'
    if (k === 'hour' && !next.minute) {
      next.minute = '00';
    }
    setParts(next);
  };

  useEffect(() => {
    setParts(initValue(value) ?? emptyValue);
  }, [value]);

  // Update the time value whenever parts change
  // Including onChange in dependencies to satisfy linter - parent should memoize this if needed
  useEffect(() => {
    if (Object.values(parts).every(Boolean)) {
      const hour = parseInt(parts.hour);
      onChange(
        `${
          parts.meridiem === 'pm' && hour < 12
            ? 12 + hour
            : parts.hour.padStart(2, '0')
        }:${parts.minute}`
      );
    }
  }, [parts, onChange]);

  return (
    <>
      <div
        className='relative flex flex-col gap-1'
        onFocusCapture={() => {
          if (!value && focusValue) {
            setParts(initValue(focusValue) ?? emptyValue);
          }
        }}
      >
        <Label>
          {label}
          {required && <span className='pl-0.5 text-brand'>*</span>}
        </Label>

        <div
          className={classNames([
            'input focus-visible:border-outline mt-0.5 flex w-full min-w-0 items-center justify-start overflow-hidden rounded border border-outline-variant bg-white focus-visible:border-grey-40 focus-visible:outline-none',
            error ? 'border-red-600' : 'border-grey-20',
            'px-2',
            leadingIcon || showClearIcon ? 'py-1.5' : 'py-2.5',
          ])}
        >
          {leadingIcon && (
            <FontAwesomeIcon
              icon={faClock as IconProp}
              className='h-4.5 w-4.5 text-gray-300'
            />
          )}
          <Segment
            items={HOURS}
            value={parts.hour}
            onChange={setPart('hour')}
            disabled={disabled}
          />
          <span className='text-center'>:</span>
          <Segment
            items={MINUTES}
            value={parts.minute}
            onChange={setPart('minute')}
            disabled={disabled}
          />
          <span className='text-center'>&nbsp;</span>
          <Segment
            items={['am', 'pm']}
            value={parts.meridiem}
            onChange={setPart('meridiem')}
            disabled={disabled}
          />
          {showClearIcon && (
            <button
              type='button'
              aria-label='Clear time'
              className={classNames([
                'ml-auto mr-0.5 rounded p-1 text-tertiary hover:text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                (disabled || !hasValue) && 'opacity-0',
              ])}
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClear();
                }
              }}
              disabled={disabled || !hasValue}
              tabIndex={disabled || !hasValue ? -1 : 0}
            >
              <FontAwesomeIcon icon={faXmark as IconProp} className='h-4 w-4' />
            </button>
          )}
        </div>

        {error && <p className='ErrorMessage'>{error}</p>}
      </div>
    </>
  );
};

type SegmentProps = {
  items: string[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

function Segment({ items, value, onChange, disabled = false }: SegmentProps) {
  return (
    <select
      className='min-w-0 appearance-none rounded bg-white px-0.5 text-right text-base text-copy-body outline-none focus:bg-brand focus:text-white'
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
    >
      <option value='' disabled>
        --
      </option>
      {items.map((option) => {
        return (
          <option key={option} value={option}>
            {option}
          </option>
        );
      })}
    </select>
  );
}

type FieldProps = Omit<Props, 'value' | 'onChange'> & {
  name: string;
};

export function TimePickerField({ name, ...props }: FieldProps) {
  const [field, meta, helpers] = useField(name);

  return (
    <TimePicker
      {...props}
      value={field.value}
      onChange={(time) => helpers.setValue(time)}
      error={meta.error}
    />
  );
}
